import { NavigationReport, StarGridLocation } from '../navigation.types';
import { StarGridView } from '../starGridConstants';

const REPORT_NAME_PREFIX = '#report/';
const NUMERIC_REPORT_HASH = /^#report-(\d+)$/;
const KNOWN_VIEWS: readonly string[] = ['home', 'infra', 'create-report', 'modify-report', 'manage-reports', 'snapshot-manager', 'onboard-dataset'];

/** Exact names preserve punctuation and Unicode; duplicate names retain an ID discriminator. */
export function reportHref(report: NavigationReport, reports: readonly NavigationReport[]): string {
  const duplicate = reports.some((candidate) => candidate.report_name === report.report_name && candidate.pnl_report_id !== report.pnl_report_id);
  return `${REPORT_NAME_PREFIX}${encodeURIComponent(report.report_name)}${duplicate ? `?id=${report.pnl_report_id}` : ''}`;
}

export function reportHrefById(reportId: number, reports: readonly NavigationReport[]): string {
  const report = reports.find((candidate) => candidate.pnl_report_id === reportId);
  return report ? reportHref(report, reports) : `#report-${reportId}`;
}

function resolveReportName(hash: string, reports: readonly NavigationReport[]): number | undefined {
  const [encodedName, query] = hash.slice(REPORT_NAME_PREFIX.length).split('?');
  let name: string;
  try {
    name = decodeURIComponent(encodedName);
  } catch {
    return undefined;
  }
  const id = new URLSearchParams(query).get('id');
  const matches = reports.filter((report) => report.report_name === name && (id === null || String(report.pnl_report_id) === id));
  return matches.length === 1 ? matches[0].pnl_report_id : undefined;
}

export function resolveStarGridHash(hash: string, reports: readonly NavigationReport[]): StarGridLocation {
  if (hash.startsWith(REPORT_NAME_PREFIX)) return { view: 'report', reportId: resolveReportName(hash, reports) };
  const numeric = NUMERIC_REPORT_HASH.exec(hash);
  if (numeric && Number.isSafeInteger(Number(numeric[1]))) return { view: 'report', reportId: Number(numeric[1]) };
  const view = hash.replace(/^#/, '');
  return { view: KNOWN_VIEWS.includes(view) ? (view as StarGridView) : 'home', reportId: undefined };
}

export function canonicalStarGridHash(hash: string, reports: readonly NavigationReport[]): string {
  const location = resolveStarGridHash(hash, reports);
  if (location.reportId !== undefined) return reportHrefById(location.reportId, reports);
  return location.view === 'report' ? hash : `#${location.view}`;
}
